import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import styles from "../../styles/styles";
import { categoriesData as staticCategoriesData } from "../../static/data";
import {
  AiOutlineHeart,
  AiOutlineSearch,
  AiOutlineShoppingCart,
} from "react-icons/ai";
import { IoIosArrowDown, IoIosArrowForward } from "react-icons/io";
import { BiMenuAltLeft } from "react-icons/bi";
import { CgProfile } from "react-icons/cg";
import DropDown from "./DropDown";
import Navbar from "./Navbar";
import { useDispatch, useSelector } from "react-redux";
import { backend_url, server } from "../../server";
import Cart from "../cart/Cart";
import Wishlist from "../Wishlist/Wishlist";
import { RxCross1 } from "react-icons/rx";
import { MdLogout } from "react-icons/md";
import logo from "../../Assets/images/logo.png";
import { fetchSiteOptions } from "../../redux/actions/siteOptions";
import NotificationBell from "../Notifications/NotificationBell";
import axios from "axios";
import { toast } from "react-toastify";
import { buildSearchUrl } from "../../utils/productSearch";

const Header = ({ activeHeading }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cart } = useSelector((state) => state.cart);
  const { wishlist } = useSelector((state) => state.wishlist);
  const { isAuthenticated, user } = useSelector((state) => state.user);
  const isSeller = user?.isSeller || false;
  const { allProducts } = useSelector((state) => state.products);
  const siteOptionsState = useSelector((state) => state.siteOptions);
  const headerOptions = siteOptionsState?.options?.global?.header;

  const [searchTerm, setSearchTerm] = useState("");
  const [searchData, setSearchData] = useState(null);
  const [active, setActive] = useState(false);
  const [dropDown, setDropDown] = useState(false);
  const [openCart, setOpenCart] = useState(false);
  const [openWishlist, setOpenWishlist] = useState(false);
  const [open, setOpen] = useState(false); // mobile menu
  const [isInputFocused, setIsInputFocused] = useState(false);

  const hasSiteOptions = Boolean(siteOptionsState?.options?.global);
  const isSiteOptionsLoading = Boolean(siteOptionsState?.loading?.global);

  useEffect(() => {
    if (!hasSiteOptions && !isSiteOptionsLoading) {
      dispatch(fetchSiteOptions());
    }
  }, [dispatch, hasSiteOptions, isSiteOptionsLoading]);

  const normalizeLink = (value, fallback = "/") => {
    if (!value || typeof value !== "string") return fallback;
    const trimmed = value.trim();
    if (
      trimmed.startsWith("http://") ||
      trimmed.startsWith("https://") ||
      trimmed.startsWith("mailto:") ||
      trimmed.startsWith("tel:")
    ) {
      return trimmed;
    }
    if (trimmed.startsWith("/")) {
      return trimmed;
    }
    return `/${trimmed}`;
  };

  const resolveImageUrl = (value) => {
    if (!value) return logo;
    if (
      value.startsWith("http://") ||
      value.startsWith("https://") ||
      value.startsWith("data:")
    ) {
      return value;
    }
    const normalized = value.startsWith("/") ? value : `/${value}`;
    return `${backend_url}${normalized}`;
  };

  const catalogCategoriesRaw =
    siteOptionsState?.options?.global?.catalog?.categories || [];

  const fallbackCategories = useMemo(() => {
    return (staticCategoriesData || []).map((item) => ({
      title: item.title,
      name: item.title,
      link:
        item.link || `/products?category=${encodeURIComponent(item.title || "")}`,
      image: item.image_Url || "",
    }));
  }, []);

  const staticImageMap = useMemo(() => {
    return fallbackCategories.reduce((acc, item) => {
      if (item.name) {
        acc[item.name] = item.image;
      }
      return acc;
    }, {});
  }, [fallbackCategories]);

  const catalogCategories = useMemo(() => {
    if (!Array.isArray(catalogCategoriesRaw) || catalogCategoriesRaw.length === 0) {
      return fallbackCategories;
    }

    const normalized = catalogCategoriesRaw
      .filter((category) => category && category.name && category.isActive !== false)
      .sort(
        (a, b) => (typeof a.order === "number" ? a.order : 0) - (typeof b.order === "number" ? b.order : 0)
      )
      .map((category) => {
        const baseName = category.name?.trim() || "";
        const fallbackLinkSource = category.slug?.trim() || baseName;
        const fallbackLink = fallbackLinkSource
          ? `/products?category=${encodeURIComponent(fallbackLinkSource)}`
          : "/products";
        const normalizedLink = normalizeLink(category.link, fallbackLink);

        return {
          title: baseName,
          name: baseName,
          link: normalizedLink,
          image: category.image || category.icon || staticImageMap[baseName] || "",
        };
      });

    return normalized.length ? normalized : fallbackCategories;
  }, [catalogCategoriesRaw, fallbackCategories, staticImageMap]);

  const navItems = headerOptions?.navLinks?.length
    ? headerOptions.navLinks
        .filter((link) => link && link.label && link.url)
        .map((link) => ({
          title: link.label,
          url: normalizeLink(link.url, "/"),
          order: typeof link.order === "number" ? link.order : 0,
          target: link.target || "_self",
        }))
    : [];

  const logoLink = normalizeLink(headerOptions?.logoLink, "/");
  const logoSrc = resolveImageUrl(headerOptions?.logo);
  const dynamicCtaLabel = headerOptions?.ctaLabel?.trim();
  const dynamicCtaLink = normalizeLink(headerOptions?.ctaLink, "/");

  const ctaLabel = dynamicCtaLabel || (isSeller ? "Go to Dashboard" : isAuthenticated ? "Go to Profile" : "Sell Now");
  const ctaLink = dynamicCtaLabel
    ? dynamicCtaLink
    : isSeller
    ? "/profile"
    : isAuthenticated
    ? "/profile"
    : "/sign-up";
  const ctaTarget =
    dynamicCtaLabel && (dynamicCtaLink.startsWith("http://") || dynamicCtaLink.startsWith("https://"))
      ? "_blank"
      : "_self";


  const searchRef = useRef(null);
  const blurTimeoutRef = useRef(null);

  // Handle search change
  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);

    const filteredProducts =
      allProducts &&
      allProducts.filter((product) =>
        product.name.toLowerCase().includes(term.toLowerCase())
      );
    setSearchData(filteredProducts);
  };

  // Detect click outside search to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setSearchData(null);
        setIsInputFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    };
  }, []);

  const handleSearchBlur = () => {
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    blurTimeoutRef.current = setTimeout(() => {
      setIsInputFocused(false);
      blurTimeoutRef.current = null;
    }, 200);
  };

  const handleSearchFocus = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setIsInputFocused(true);
  };

  const submitHeaderSearch = (termOverride) => {
    const term = (termOverride ?? searchTerm).trim();
    if (!term) return;
    setSearchData(null);
    setSearchTerm("");
    setIsInputFocused(false);
    setOpen(false);
    navigate(buildSearchUrl(term));
    scrollToTopOnNavigate();
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submitHeaderSearch();
    }
  };

  const scrollToTopOnNavigate = () => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  };

  const logoutHandler = async () => {
    try {
      const { data } = await axios.get(`${server}/user/logout`, {
        withCredentials: true,
      });
      dispatch({ type: "clearCart" });
      localStorage.removeItem("cartItems");
      toast.success(data?.message || "Logged out successfully.");
      setOpen(false);
      window.location.href = "/login";
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to logout");
    }
  };

  // Sticky header on scroll - smoothly transition top position
  useEffect(() => {
    const handleScroll = () => {
      // Top section height: 50px (h-[50px]) + 48px (pt-12) + 48px (pb-12) = 146px
      // When scrolled past top section, move menu to top (0)
      if (window.scrollY > 146) {
        setActive(true);
      } else {
        setActive(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!openCart) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpenCart(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [openCart]);

  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);


 return (
    <>
      {/* Top section - Logo, Search, CTA */}
      <div className={`${styles.section} hidden 800px:block`}>
        <div className="h-[50px] pt-12 pb-12 flex items-center justify-between">
          <div>
            {logoLink.startsWith("http") || logoLink.startsWith("mailto:") || logoLink.startsWith("tel:") ? (
              <a href={logoLink} target={logoLink.startsWith("http") ? "_blank" : "_self"} rel="noopener noreferrer">
                <img src={logoSrc} className="c-main-logo" alt="logo" />
              </a>
            ) : (
              <Link to={logoLink}>
                <img src={logoSrc} className="c-main-logo" alt="logo" />
              </Link>
            )}
          </div>

          {/* Search box */}
          <div ref={searchRef} className="w-[50%] relative">
            <input
              type="search"
              enterKeyHint="search"
              placeholder="Search for product..."
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
              onKeyDown={handleSearchKeyDown}
              className=" w-full py-2 rounded border border-[#38513B] focus:outline-none pl-2 pr-10 text-base"
            />
            <button
              type="button"
              onClick={() => submitHeaderSearch()}
              className="absolute right-2 top-1.5 text-slate-600 hover:text-[#38513b]"
              aria-label="Search"
            >
              <AiOutlineSearch size={30} />
            </button>

            {isInputFocused && searchData && searchData.length !== 0 && (
              <div className="absolute min-h-[30vh] shadow-sm-2 z-[9] p-4 w-[100%] searchwind bg-white rounded border border-slate-200">
                {searchData.slice(0, 8).map((i) => (
                  <Link
                    to={`/product/${i._id}`}
                    key={i._id}
                    className="block w-full flex items-start py-3 hover:bg-slate-50 rounded"
                    onClick={() => {
                      setSearchData(null);
                      setSearchTerm("");
                      setIsInputFocused(false);
                    }}
                  >
                    <img
                      src={i.images?.[0] ? `${backend_url}${i.images[0]}` : logo}
                      alt=""
                      className="w-[40px] h-[40px] mr-[10px] object-contain shrink-0"
                    />
                    <h1 className="text-sm font-medium text-slate-800 truncate">{i.name}</h1>
                  </Link>
                ))}
                {searchTerm.trim() ? (
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => submitHeaderSearch()}
                    className="mt-2 w-full rounded-lg bg-[#38513b]/10 px-3 py-2.5 text-left text-sm font-semibold text-[#38513b] hover:bg-[#38513b]/15"
                  >
                    View all results for &ldquo;{searchTerm.trim()}&rdquo;
                  </button>
                ) : null}
              </div>
            )}
          </div>

          {/* Search box end */}

          {/* Become a Seller */}
          {ctaLink.startsWith("http") || ctaLink.startsWith("mailto:") || ctaLink.startsWith("tel:") ? (
            <a
              href={ctaLink}
              target={ctaTarget}
              rel={ctaTarget === "_blank" ? "noopener noreferrer" : undefined}
              className={`${styles.button} text-[#fff] flex items-center`}
            >
              {ctaLabel} <IoIosArrowForward className="ml-1" />
            </a>
          ) : (
            <Link
              to={ctaLink}
              className={`${styles.button} text-[#fff] flex items-center`}
              onClick={scrollToTopOnNavigate}
            >
              {ctaLabel} <IoIosArrowForward className="ml-1" />
            </Link>
          )}
          {/* Become a Seller end */}
        </div>
      </div>

      {/*  2nd part of header start */}
      <div
        className={`${
          active == true ? "shadow-sm" : ""
        } transition-all duration-300 ease-in-out hidden 800px:flex items-center justify-between w-full bg-[#38513B] h-[70px] relative`}
      >
        <div
          className={`${styles.section} relative ${styles.noramlFlex} justify-between`}
        >
          {/* Catagories */}
          <div onClick={() => setDropDown(!dropDown)}>
            <div className="relative h-[55px] mt-[16px] w-[270px] hidden 1000px:block">
              <BiMenuAltLeft size={30} className="absolute top-3 left-2" />
              <button
                className={`h-[100%] w-full flex justify-between items-center pl-10 bg-[#CCBEA1] font-sans text-lg font-[500] select-none rounded-t-md`}
              >
                All Categories
              </button>
              <IoIosArrowDown
                size={20}
                className="absolute right-2 top-4 cursor-pointer"
                onClick={() => setDropDown(!dropDown)}
              />
              {dropDown && (
                <DropDown categoriesData={catalogCategories} setDropDown={setDropDown} />
              )}
            </div>
          </div>

          {/* NavItems */}
          <div className={`${styles.noramlFlex}`}>
            <Navbar active={activeHeading} items={navItems} />
          </div>

          <div className="flex">
            <div className={`${styles.noramlFlex}`}>
              <div
                className="relative cursor-pointer mr-[15px]"
                onClick={() => setOpenWishlist(true)}
              >
                <AiOutlineHeart size={30} color="rgb(255 255 255 / 83%)" />
                <span className="absolute right-0 top-0 rounded-full bg-[#CCBEA1] w-4 h-4 top right p-0 m-0 text-black font-mono text-[12px] leading-tight text-center">
                  {wishlist && wishlist.length}
                </span>
              </div>
            </div>

            <div className={`${styles.noramlFlex}`}>
              <div
                className="relative cursor-pointer mr-[15px]"
                onClick={() => setOpenCart(true)}
              >
                <AiOutlineShoppingCart
                  size={30}
                  color="rgb(255 255 255 / 83%)"
                />
                <span className="absolute right-0 top-0 rounded-full bg-[#CCBEA1] w-4 h-4 top right p-0 m-0 text-black font-mono text-[12px] leading-tight text-center">
                  {cart && cart.length}
                </span>
              </div>
            </div>

            {/* Notifications */}
            {isAuthenticated && (
              <div className={`${styles.noramlFlex}`}>
                <NotificationBell />
              </div>
            )}
            {/* avatar */}
            <div className={`${styles.noramlFlex}`}>
              <div className="relative cursor-pointer mr-[15px]">
                {isAuthenticated ? (
                  <Link to="/profile" onClick={scrollToTopOnNavigate}>
                    <img
                      src={`${backend_url}${user.avatar || "default-avatar.png"}`}
                      className="w-[35px] h-[35px] rounded-full"
                      alt=""
                      onError={(e) => {
                        e.target.src = `${backend_url}default-avatar.png`;
                      }}
                    />
                  </Link>
                ) : (
                  <Link to="/login">
                    <CgProfile size={30} color="rgb(255 255 255 / 83%)" />
                  </Link>
                )}
              </div>
            </div>
            {/* Avatar end */}
            {/* card  popup start */}
            {openCart ? <Cart setOpenCart={setOpenCart} /> : null}
            {/* card popup end */}

            {/* Wish list pop uo Start */}
            {openWishlist ? (
              <Wishlist setOpenWishlist={setOpenWishlist} />
            ) : null}
            {/* Wish list pop uo end */}
          </div>
        </div>
      </div>

      {/* Mobile Header */}
      <div
        className={`${
          active === true ? "shadow-sm" : ""
        } w-full mob-menu bg-[#fff] shadow-sm 800px:hidden transition-all duration-300 fixed top-0 left-0 right-0 z-[100000]`}
      >
        <div className="w-full flex items-center justify-between">
          <div>
            <BiMenuAltLeft
              size={40}
              className="ml-4"
              onClick={() => setOpen(true)}
            />
          </div>
          <div>
            {logoLink.startsWith("http") || logoLink.startsWith("mailto:") || logoLink.startsWith("tel:") ? (
              <a
                href={logoLink}
                target={logoLink.startsWith("http") ? "_blank" : "_self"}
                rel={logoLink.startsWith("http") ? "noopener noreferrer" : undefined}
              >
                <img
                  src={logoSrc}
                  alt="logo"
                  className="my-3 cursor-pointer c-main-logo"
                />
              </a>
            ) : (
              <Link to={logoLink}>
                <img
                  src={logoSrc}
                  alt="logo"
                  className="my-3 cursor-pointer c-main-logo"
                />
              </Link>
            )}
          </div>

          <div className="mr-[20px] flex items-center gap-3">
            {isAuthenticated ? <NotificationBell variant="light" /> : null}
            <div
              className="relative"
              onClick={() => setOpenCart(true)}
            >
              <AiOutlineShoppingCart size={30} />
              <span className="absolute right-0 top-0 rounded-full bg-[#CCBEA1] w-4 h-4 top right p-0 m-0 text-white font-mono text-[12px]  leading-tight text-center">
                {cart && cart.length}
              </span>
            </div>
          </div>
          {/* cart popup */}
          {openCart ? <Cart setOpenCart={setOpenCart} /> : null}

          {/* wishlist popup */}
          {openWishlist ? <Wishlist setOpenWishlist={setOpenWishlist} /> : null}
        </div>
      </div>
      <div className="h-[50px] 800px:hidden" />

      {/* Mobile menu drawer — classic white panel (wishlist + close, search, links, tan CTA, avatar) */}
      {open ? (
        <div className="fixed inset-0 z-[100001] 800px:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            aria-label="Close menu"
            onClick={() => setOpen(false)}
          />

          <aside className="absolute inset-y-0 left-0 flex w-[min(72vw,300px)] flex-col bg-white shadow-xl">
            {/* Top: wishlist + close */}
            <div className="flex shrink-0 items-center justify-between px-4 pt-[max(0.75rem,env(safe-area-inset-top))] pb-3">
              <button
                type="button"
                className="relative flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-900 shadow-sm"
                aria-label="Wishlist"
                onClick={() => {
                  setOpenWishlist(true);
                  setOpen(false);
                }}
              >
                <AiOutlineHeart size={22} strokeWidth={2} />
                <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#CCBEA1] px-0.5 text-[11px] font-bold text-slate-900">
                  {wishlist?.length ?? 0}
                </span>
              </button>
              <button
                type="button"
                className="flex h-10 w-10 items-center justify-center text-slate-900"
                aria-label="Close menu"
                onClick={() => setOpen(false)}
              >
                <RxCross1 size={28} />
              </button>
            </div>

            <div ref={searchRef} className="relative shrink-0 px-4 pb-4">
              <input
                type="search"
                enterKeyHint="search"
                placeholder="Search for products"
                className="h-11 w-full rounded-lg border border-[#38513B] px-3 pr-11 text-base text-slate-900 placeholder:text-slate-400 focus:border-[#38513B] focus:outline-none focus:ring-1 focus:ring-[#38513B]/30"
                value={searchTerm}
                onChange={handleSearchChange}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
                onKeyDown={handleSearchKeyDown}
              />
              <button
                type="button"
                onClick={() => submitHeaderSearch()}
                className="absolute h-11 inset-y-0 right-4 flex w-8 items-center justify-center text-slate-500"
                aria-label="Search"
              >
                <AiOutlineSearch size={20} className="shrink-0" />
              </button>
              {isInputFocused && searchData && searchData.length > 0 ? (
                <div className="absolute left-4 right-4 top-full z-20 mt-1 max-h-[28vh] overflow-y-auto rounded-lg border border-slate-200 bg-white p-2 shadow-lg">
                  {searchData.slice(0, 8).map((i) => (
                    <Link
                      key={i._id}
                      to={`/product/${i._id}`}
                      className="flex items-center gap-2 rounded-md px-2 py-2 hover:bg-slate-50"
                      onClick={() => {
                        setOpen(false);
                        setSearchData(null);
                        setSearchTerm("");
                        setIsInputFocused(false);
                      }}
                    >
                      <img
                        src={i.images?.[0] ? `${backend_url}${i.images[0]}` : logo}
                        alt=""
                        className="h-9 w-9 shrink-0 object-contain"
                      />
                      <span className="line-clamp-2 text-xs font-medium text-slate-800">{i.name}</span>
                    </Link>
                  ))}
                  {searchTerm.trim() ? (
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => submitHeaderSearch()}
                      className="mt-1 w-full rounded-md px-2 py-2 text-left text-xs font-semibold text-[#38513b] hover:bg-slate-50"
                    >
                      View all results for &ldquo;{searchTerm.trim()}&rdquo;
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>

            <nav className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
              <Navbar
                active={activeHeading}
                items={navItems}
                variant="mobile"
                onItemClick={() => setOpen(false)}
              />
            </nav>

            <div className="shrink-0 space-y-4 border-t border-slate-100 bg-white px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
              {ctaLink.startsWith("http") ||
              ctaLink.startsWith("mailto:") ||
              ctaLink.startsWith("tel:") ? (
                <a
                  href={ctaLink}
                  target={ctaTarget}
                  rel={ctaTarget === "_blank" ? "noopener noreferrer" : undefined}
                  className="flex h-11 w-full items-center justify-center gap-1 rounded-lg bg-[#CCBEA1] text-sm font-semibold text-black shadow-sm"
                  onClick={() => setOpen(false)}
                >
                  {ctaLabel} <IoIosArrowForward size={16} />
                </a>
              ) : (
                <Link
                  to={ctaLink}
                  className="flex h-11 w-full items-center justify-center gap-1 rounded-lg bg-[#CCBEA1] text-sm font-semibold text-black shadow-sm"
                  onClick={() => {
                    setOpen(false);
                    scrollToTopOnNavigate();
                  }}
                >
                  {ctaLabel} <IoIosArrowForward size={16} />
                </Link>
              )}

              {isAuthenticated ? (
                <div className="flex items-center justify-center gap-3 pt-1">
                  <Link
                    to="/profile"
                    onClick={() => {
                      setOpen(false);
                      scrollToTopOnNavigate();
                    }}
                    className="block shrink-0 rounded-full p-0.5 ring-2 ring-teal-400 ring-offset-2 ring-offset-white"
                  >
                    <img
                      src={`${backend_url}${user.avatar || "default-avatar.png"}`}
                      alt=""
                      className="h-14 w-14 rounded-full object-cover"
                      onError={(e) => {
                        e.target.src = `${backend_url}default-avatar.png`;
                      }}
                    />
                  </Link>
                  <button
                    type="button"
                    onClick={logoutHandler}
                    className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 shadow-sm hover:border-slate-400 hover:bg-slate-50"
                    aria-label="Log out"
                  >
                    <MdLogout size={14} className="shrink-0" aria-hidden />
                    Logout
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <Link
                    to="/login"
                    className="flex h-9 items-center justify-center rounded-lg border border-[#38513B] bg-white text-xs font-semibold text-[#38513B] hover:bg-slate-50"
                    onClick={() => setOpen(false)}
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/sign-up"
                    className="flex h-9 items-center justify-center rounded-lg bg-[#38513B] text-xs font-semibold text-white hover:bg-[#2f4232]"
                    onClick={() => setOpen(false)}
                  >
                    Sign up
                  </Link>
                </div>
              )}
            </div>
          </aside>
        </div>
      ) : null}
    </>
  );
};

export default Header;
